<kpa-loader></kpa-loader>

<div class="signin-page">

	<!-- ── Left: illustration panel ─────────────────────────────────────────── -->
	<div class="signin-left" aria-hidden="true"></div>

	<!-- ── Right: form panel ────────────────────────────────────────────────── -->
	<div class="signin-right">
		<div class="signin-form-wrap">

			<!-- Branding -->
			<div class="signin-branding">
				<h1 class="signin-title">Risk Management Center</h1>
				<h2 class="signin-subtitle">Employee Login Portal</h2>
			</div>

			<!-- Error banner -->
			<div
				*ngIf="signInActionFailed"
				class="kpa-login-invalid-error">
				<div class="error-content">
					<kendo-svg-icon [icon]="warningTriangleIcon"></kendo-svg-icon>
					<p>{{ invalidErrorMessage }}</p>
				</div>
			</div>

			<!-- ── Login form ────────────────────────────────────────────────── -->
			<form
				id="loginForm"
				*ngIf="!showForgotPasswordForm"
				class="signin-form"
				[formGroup]="loginForm">

				<!-- Company ID -->
				<kendo-formfield>
					<kendo-label
						labelCssClass="signin-field-label"
						[for]="companyId"
						text="Company ID"></kendo-label>
					<kendo-textbox
						formControlName="companyId"
						[fillMode]="'outline'"
						[clearButton]="true"
						#companyId
						(blur)="onCompanyIdBlur()"
						required>
						<ng-template kendoTextBoxPrefixTemplate>
							<kendo-svg-icon [icon]="companyIdIcon" class="field-icon"></kendo-svg-icon>
						</ng-template>
					</kendo-textbox>
					<validation-errors
						[control]="loginForm.controls['companyId']">
					</validation-errors>
				</kendo-formfield>

				<!-- Continue (initial / checking) -->
				<div
					class="signin-actions"
					*ngIf="loginMode === 'initial' || loginMode === 'checking'">
					<button
						id="continue-btn"
						class="signin-btn"
						kendoButton
						[disabled]="loginMode === 'checking'"
						[size]="'large'"
						[rounded]="'small'"
						[fillMode]="'solid'"
						(click)="onContinue()">
						{{ loginMode === 'checking' ? 'Checking...' : 'Continue' }}
						<kendo-svg-icon
							*ngIf="loginMode === 'initial'"
							[icon]="chevronRightIcon"></kendo-svg-icon>
					</button>
				</div>

				<!-- Username (standard / dual) -->
				<kendo-formfield *ngIf="loginMode === 'standard' || loginMode === 'dual'">
					<kendo-label
						labelCssClass="signin-field-label"
						[for]="username"
						text="Username"></kendo-label>
					<kendo-textbox
						[fillMode]="'outline'"
						formControlName="username"
						[clearButton]="true"
						#username
						required>
						<ng-template kendoTextBoxPrefixTemplate>
							<kendo-svg-icon [icon]="usernameIcon" class="field-icon"></kendo-svg-icon>
						</ng-template>
					</kendo-textbox>
					<validation-errors
						[control]="loginForm.controls['username']">
					</validation-errors>
				</kendo-formfield>

				<!-- Password (standard / dual) -->
				<kendo-formfield *ngIf="loginMode === 'standard' || loginMode === 'dual'">
					<kendo-label
						labelCssClass="signin-field-label"
						[for]="password"
						text="Password"></kendo-label>
					<kendo-textbox
						[fillMode]="'outline'"
						formControlName="password"
						#password
						[clearButton]="true"
						type="password"
						required>
						<ng-template kendoTextBoxPrefixTemplate>
							<kendo-svg-icon [icon]="passwordIcon" class="field-icon"></kendo-svg-icon>
						</ng-template>
					</kendo-textbox>
					<validation-errors
						[control]="loginForm.controls['password']">
					</validation-errors>
				</kendo-formfield>

				<!-- Remember me + Forgot Password (standard / dual) -->
				<div
					class="signin-forgot-wrap"
					*ngIf="loginMode === 'standard' || loginMode === 'dual'">
					<div class="remember-me-wrap">
						<input
							*ngIf="showRememberPwd"
							id="rememberMeCheckbox"
							type="checkbox"
							kendoCheckBox
							formControlName="rememberMe" />
						<kendo-label
							*ngIf="showRememberPwd"
							for="rememberMeCheckbox"
							class="k-checkbox-label"
							text="Remember me"></kendo-label>
					</div>
					<button
						id="forgot-btn"
						type="button"
						class="forgot-password-btn"
						(click)="onForgotPassword()">
						Forgot Password?
					</button>
				</div>

				<!-- Sign in (standard / dual) -->
				<div
					class="signin-actions signin-actions--primary"
					*ngIf="loginMode === 'standard' || loginMode === 'dual'">
					<button
						id="signin-btn"
						class="signin-btn"
						kendoButton
						[disabled]="signInInitiated"
						[size]="'large'"
						[rounded]="'small'"
						[fillMode]="'solid'"
						(click)="onSubmit()">
						{{ signInInitiated ? 'Signing in...' : 'Sign in' }}
					</button>
				</div>

				<!-- SSO section (dual / sso) -->
				<ng-container *ngIf="loginMode === 'dual' || loginMode === 'sso'">
					<!-- Divider only makes sense in dual mode (email OR SSO); hide it when SSO is the only option -->
					<div class="sso-divider" *ngIf="loginMode === 'dual'">
						<span class="sso-divider-line"></span>
						<span class="sso-divider-text">Or continue with</span>
						<span class="sso-divider-line"></span>
					</div>
					<!-- In sso-only mode the SSO button is the sole action, so give it the same 50px
					     credential→button gap as Sign in has in standard/dual. In dual mode it's already
					     inside the button group (below the divider), so no extra gap there. -->
					<div class="signin-actions" [class.signin-actions--primary]="loginMode === 'sso'">
						<button
							id="sso-signin-btn"
							class="signin-btn"
							kendoButton
							[disabled]="ssoInitiated"
							[size]="'large'"
							[rounded]="'small'"
							[fillMode]="'solid'"
							(click)="onSignInWithSso()">
							{{ ssoInitiated ? 'Redirecting...' : 'Sign in with SSO' }}
						</button>
					</div>
				</ng-container>

			</form>

			<!-- ── Forgot-password form ──────────────────────────────────────── -->
			<form
				id="resetForm"
				*ngIf="showForgotPasswordForm"
				class="signin-form"
				[formGroup]="loginForm">

				<kendo-formfield>
					<kendo-label
						labelCssClass="signin-field-label"
						[for]="companyId"
						text="Company ID"></kendo-label>
					<kendo-textbox
						formControlName="companyId"
						[fillMode]="'outline'"
						[clearButton]="true"
						#companyId
						required>
						<ng-template kendoTextBoxPrefixTemplate>
							<kendo-svg-icon [icon]="companyIdIcon" class="field-icon"></kendo-svg-icon>
						</ng-template>
					</kendo-textbox>
					<validation-errors
						[control]="loginForm.controls['companyId']">
					</validation-errors>
				</kendo-formfield>

				<kendo-formfield>
					<kendo-label
						labelCssClass="signin-field-label"
						[for]="username"
						text="Username"></kendo-label>
					<kendo-textbox
						[fillMode]="'outline'"
						formControlName="username"
						[clearButton]="true"
						#username
						required>
						<ng-template kendoTextBoxPrefixTemplate>
							<kendo-svg-icon [icon]="usernameIcon" class="field-icon"></kendo-svg-icon>
						</ng-template>
					</kendo-textbox>
					<validation-errors
						[control]="loginForm.controls['username']">
					</validation-errors>
				</kendo-formfield>

				<div class="signin-actions forgot-actions">
					<button
						id="cancel-btn"
						type="button"
						class="signin-btn signin-btn--secondary"
						kendoButton
						[size]="'large'"
						[rounded]="'small'"
						[fillMode]="'solid'"
						(click)="onCancel()">
						Cancel
					</button>
					<button
						id="request-btn"
						type="button"
						class="signin-btn"
						kendoButton
						[disabled]="validateEmailInitiated"
						[size]="'large'"
						[rounded]="'small'"
						[fillMode]="'solid'"
						(click)="onRequestPasswordReset()">
						Request Password
					</button>
				</div>

			</form>

		</div>
	</div>

</div>

<kpa-forgot-password
	[dialogOpened]="showResetPasswordDialog"
	[employeeId]="currentEmployeeId"
	(dialogOpenedChange)="resendCredentials($event)"
	(closeDialog)="closeDialog()"></kpa-forgot-password>

<kpa-contact-admin
	[dialogOpened]="showContactAdminDialog"
	(closeDialog)="closeDialog()"></kpa-contact-admin>
